import * as React from 'react';
import {FloatingDelayGroup} from '@floating-ui/react';
import {TooltipGroupContext} from './Tooltip';
import {TOOLTIP_DELAY} from './useTooltip';
import type {UseTooltipProps} from './useTooltip';

import type {TooltipProps} from './_types';

type TooltipGroupProps = {
  children: React.ReactNode;
  /**
   * The initial opening delay for the Tooltips in the group
   *
   * Defaults to `medium`
   */
  delay?: UseTooltipProps['delay'];
  /**
   * The density for all children `Tooltip` components. Use `'compact'` sparingly. Using `'compact'` will also set `hasArrow={false}` to remove the arrow. Defaults to `'comfortable'`.
   */
  density?: TooltipProps['density'];
  /**
   * Sets the color variant for all children `Tooltip` components.
   */
  colorVariant?: TooltipProps['colorVariant'];
  /**
   * Sets `hasArrow` for all children `Tooltip` components. Defaults to `true`.
   */
  hasArrow?: TooltipProps['hasArrow'];
  /**
   * Number of pixels to shift all the children `Tooltip` components horizontally.
   */
  horizontalOffset?: number;
  /**
   * Makes child `Tooltip` components hoverable by reducing the close delay.
   */
  isHoverable?: boolean;
  /**
   * Sets `placement` for all children `Tooltip` components.
   */
  placement?: TooltipProps['placement'];
  /**
   * Number of pixels to shift all the children `Tooltip` components vertically.
   */
  verticalOffset?: number;
  /**
   * Sets `shouldEscapeViewport` for all children `Tooltip` components.
   */
  shouldEscapeViewport?: TooltipProps['shouldEscapeViewport'];
};

/**
 * # TooltipGroup
 *
 * Creates a TooltipGroup that shares delay timings across the group.
 * Only one Tooltip within the group will display at a time.
 *
 * Please be aware that `TooltipGroup` uses react context and the `Tooltip`s in the group do not need to be immediate children of the `TooltipGroup`.
 *
 * ## Usage
 *
 * ```jsx
 * import {Tooltip, TooltipGroup} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return (
 *    <TooltipGroup>
 *      <Tooltip label="Tooltip content">Hover me</Tooltip>
 *      <Tooltip label="Tooltip content">Hover me</Tooltip>
 *    </TooltipGroup>
 *  )
 * }
 * ```
 */
export const TooltipGroup = ({
  delay: delayProp = 'medium',
  children,
  colorVariant,
  density,
  placement,
  hasArrow,
  horizontalOffset,
  isHoverable,
  verticalOffset,
  shouldEscapeViewport,
}: TooltipGroupProps) => {
  const delay = isHoverable
    ? TOOLTIP_DELAY.hoverable
    : TOOLTIP_DELAY[delayProp];

  const contextValue = React.useMemo(
    () => ({
      colorVariant,
      density,
      placement,
      hasArrow,
      horizontalOffset,
      isHoverable,
      verticalOffset,
      shouldEscapeViewport,
    }),
    [
      colorVariant,
      density,
      placement,
      hasArrow,
      horizontalOffset,
      isHoverable,
      verticalOffset,
      shouldEscapeViewport,
    ]
  );

  return (
    <FloatingDelayGroup delay={delay} timeoutMs={TOOLTIP_DELAY.hoverable.close}>
      <TooltipGroupContext.Provider value={contextValue}>
        {children}
      </TooltipGroupContext.Provider>
    </FloatingDelayGroup>
  );
};
